import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { getProgramProgressionStatusIcon } from "@modules/programs/programSubscriber/utils/programSubscriberUtils"
import { ProgramProgressionStatus } from "@modules/programs/types/programOnOffTypes"
import { Circles } from "@modules/ui/components/circles"
import dayjs from "dayjs"
import { useMemo } from "react"
import { Text } from "@/components/ui/baseUi/text"

type ProgramSubscriberDetailsContentOnOffStatusProps = {
  subscriberDetails: ProgramSubscriberEntity
  className?: string
} & React.ComponentProps<"div">

export function ProgramSubscriberDetailsContentOnOffStatus({
  subscriberDetails,
  className,
  ...props
}: ProgramSubscriberDetailsContentOnOffStatusProps) {
  const subscriptionInfos = subscriberDetails.getProgramSubscriptionOnOffStatusInfos()
  const isPaused = subscriberDetails.onOffProgramProgression?.status === ProgramProgressionStatus.STOPPED
  const isCompleted = subscriberDetails.onOffProgramProgression?.status === ProgramProgressionStatus.COMPLETED
  const mainTextColor = isCompleted
    ? "var(--textDefault-heading)"
    : isPaused
      ? "var(--textDefault-caption)"
      : "var(--textDefault-heading)"

  const fillRatePoints = useMemo(() => {
    if (
      !subscriberDetails.onOffProgramProgression ||
      subscriberDetails.onOffProgramProgression.status === ProgramProgressionStatus.NOT_STARTED
    )
      return 0

    const fillRate =
      !subscriberDetails.onOffProgramProgression.currentIndex || !subscriberDetails.onOffProgramProgression.duration
        ? 0
        : ((subscriberDetails.onOffProgramProgression.currentIndex + 1) /
            subscriberDetails.onOffProgramProgression.duration) *
          100
    const fillRatePointsValue = Math.round(fillRate / 10)
    return Math.min(10, Math.max(0, fillRatePointsValue))
  }, [subscriberDetails.onOffProgramProgression])

  return (
    <div className={cn("flex w-full flex-col gap-2", className)} {...props}>
      <div className="flex flex-row">
        <div className="flex w-1/2 flex-col pr-3">
          {/*STATUS */}
          <div className="flex flex-col gap-2">
            <div className="flex w-full items-baseline gap-2">
              <Text fontStyle="BS" className="text-textDefault-caption">
                {word("program.subscribers.subscriptionDetails.status.title")}
              </Text>
              <div className="flex grow border-t border-borderDefault" />
            </div>
            <div className="flex flex-row items-center gap-2">
              {subscriberDetails.onOffProgramProgression
                ? getProgramProgressionStatusIcon(
                    subscriberDetails.onOffProgramProgression.status,
                    subscriptionInfos.color,
                  )
                : null}
              <div className="flex flex-col gap-0">
                <Text fontStyle="LL" className="text-textDefault-heading">
                  {subscriptionInfos.label}
                </Text>
                {subscriptionInfos.information ? (
                  <Text fontStyle="BS" className="text-textDefault-caption">
                    {subscriptionInfos.information}
                  </Text>
                ) : null}
              </div>
            </div>
          </div>
          {/* DATE OF PURCHASE */}
          <div className="my-auto flex flex-col gap-2">
            <div className="flex w-full items-baseline gap-2">
              <Text fontStyle="BS" className="text-textDefault-caption">
                {word("program.subscribers.subscriptionDetails.dateOfPurchase.label")}
              </Text>
              <div className="flex grow border-t border-borderDefault" />
            </div>
            <Text fontStyle="LL" className="text-textDefault-heading">
              {dayjs(subscriberDetails.subscriptionDate).format("L")}
            </Text>
          </div>
        </div>
        <div className="flex w-1/2 flex-col gap-2 pl-3">
          {/* PROGRESS */}
          <div className="flex flex-row items-baseline gap-2">
            <Text fontStyle="BS" className="text-textDefault-caption">
              {word("program.subscribers.subscriptionDetails.progress.label")}
            </Text>
            <div className="flex grow border-t border-borderDefault" />
          </div>
          {/* CIRCLES */}
          <div className="flex flex-col items-center gap-2">
            {subscriberDetails.onOffProgramProgression ? (
              <>
                <Text fontStyle="BM" className="mb-1 text-textDefault-heading">
                  {`${fillRatePoints} / ${subscriberDetails.onOffProgramProgression.duration} ${word("global.day.label", { count: subscriberDetails.onOffProgramProgression.duration })}`}
                </Text>

                <Circles count={10} filled={fillRatePoints} progressionstyle="circle" color={mainTextColor} />
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
